import React, { useState } from 'react';
import axios from 'axios';
import HeroSection from '../components/HeroSection';
import SEO from '../components/SEO';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const HERO_IMG = '/images/charities-hero-2.jpg';

// Keep ids kebab-safe + mirrored on the backend (VOLUNTEER_EVENT_LABELS /
// VOLUNTEER_ROLE_LABELS). Add new ones in both places.
const EVENTS = [
  { id: 'heritage_weekend', label: 'Glossop Heritage Weekend', blurb: 'A weekend celebrating the town\'s heritage, markets and music — multiple sites across Glossop.' },
  { id: 'glossfest',        label: 'GlossFest',               blurb: 'Our summer live-music festival in Manor Park. Bar, gates, marshalling and the big take-down.' },
  { id: 'bonfire_night',    label: 'Bonfire Night',           blurb: 'Over 3,000 people in the park — car-park marshalling, pre-event build, bars, gate stewards.' },
  { id: 'any_event',        label: 'Any event',               blurb: 'Happy to help wherever an extra pair of hands is needed.' },
];

const ROLES = [
  { id: 'setup',            label: 'Set-up',          blurb: 'The day before / morning of the event. Fencing, bars, signage, marquees.' },
  { id: 'marshalling',      label: 'Marshalling',     blurb: 'High-vis duty: gates, car parks, directing attendees, event safety.' },
  { id: 'bar',              label: 'Bar',             blurb: 'Serving drinks, running floats, carrying barrels. Over-18s only.' },
  { id: 'clearing_up',      label: 'Clearing up',     blurb: 'The least glamorous but most needed shift — litter picking, de-rig and reset.' },
  { id: 'anywhere_needed',  label: 'Wherever needed', blurb: 'You\'ll turn up and we\'ll point you at whatever needs doing.' },
];

const EMPTY = {
  name: '',
  email: '',
  phone: '',
  location: '',
  availability: '',
  message: '',
};

function ChoiceCard({ checked, onChange, label, blurb, testid }) {
  return (
    <label
      className={`flex items-start gap-3 p-4 border cursor-pointer transition-colors select-none ${
        checked ? 'border-[#F0A500] bg-[#FEF7E6]' : 'border-zinc-300 bg-white hover:border-[#F0A500]/60'
      }`}
      data-testid={testid}
    >
      <Checkbox checked={checked} onCheckedChange={(c) => onChange(c === true)} className="mt-1" />
      <div className="min-w-0">
        <div className="text-sm font-bold text-[#0A0A0A]">{label}</div>
        {blurb && <div className="text-xs text-[#52525B] mt-0.5 leading-relaxed">{blurb}</div>}
      </div>
    </label>
  );
}

export default function VolunteerPage() {
  const [form, setForm] = useState(EMPTY);
  const [events, setEvents] = useState(() => new Set());
  const [roles, setRoles] = useState(() => new Set());
  const [notRobot, setNotRobot] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const toggle = (set, setter) => (id, next) => {
    const copy = new Set(set);
    if (next) copy.add(id); else copy.delete(id);
    setter(copy);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await axios.post(`${API}/volunteer`, {
        ...form,
        location: form.location || null,
        availability: form.availability || null,
        message: form.message || null,
        events: [...events],
        roles: [...roles],
        consent: true,
      });
      setSubmitted(true);
      setForm(EMPTY);
      setEvents(new Set());
      setRoles(new Set());
      setNotRobot(false);
    } catch (err) {
      if (err.response?.data?.detail) {
        const d = err.response.data.detail;
        setError(typeof d === 'string' ? d : 'Please check the required fields and try again.');
      } else {
        setError('Something went wrong. Please try again or email volunteers@glossoproundtable.org directly.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const canSubmit = !submitting && !!form.name.trim() && !!form.email.trim() && !!form.phone.trim() && notRobot;

  return (
    <div data-testid="volunteer-page">
      <SEO
        title="Volunteer"
        description="Lend a hand at a Glossop Round Table event — Heritage Weekend, GlossFest or Bonfire Night. Set-up, marshalling, bar work, clearing up. No experience needed."
        path="/volunteer"
        keywords={['volunteer Glossop', 'Glossop Round Table volunteer', 'help at Bonfire Night Glossop', 'GlossFest volunteer', 'Heritage Weekend volunteer']}
      />
      <HeroSection
        image={HERO_IMG}
        title="Volunteer With Us"
        subtitle="Our events only happen because people lend a hand — fancy joining the crew?"
        fullHeight={false}
        alt="Glossop Round Table members and the Mayor of Glossop holding a large Round Table cheque for £20,200 made out to Glossop & District Charities"
      />

      {/* Intro */}
      <section className="py-16 sm:py-20 bg-white px-4">
        <div className="max-w-3xl mx-auto">
          <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#F0A500] block mb-3">
            Call for volunteers
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#0A0A0A] mb-6">
            Help put on the events the town loves
          </h2>
          <div className="space-y-4 text-base text-[#52525B] leading-relaxed">
            <p>
              Glossop &amp; District Round Table 916 is a small group of volunteers — we can only run events the scale of Bonfire Night, GlossFest and Heritage Weekend because the local community gets stuck in alongside us.
            </p>
            <p>
              <strong className="text-[#0A0A0A]">We&rsquo;re looking for people happy to help with:</strong> event set-up (fencing, bars, signage), marshalling the car parks and gates, serving behind the bars, and the all-important clearing up and de-rig. No experience needed — we&rsquo;ll show you the ropes on the day.
            </p>
            <p>
              Pop your details in below and we&rsquo;ll get in touch when the next event is being planned. Any time you can spare &mdash; a couple of hours, a shift, or a whole weekend &mdash; is hugely appreciated.
            </p>
          </div>
        </div>
      </section>

      {/* Form */}
      <section className="py-16 sm:py-20 bg-[#F4F4F5] px-4">
        <div className="max-w-3xl mx-auto">
          {submitted ? (
            <div className="bg-white p-8 border-l-4 border-[#F0A500]" data-testid="volunteer-success">
              <h2 className="text-2xl sm:text-3xl font-bold text-[#0A0A0A] mb-3">Thanks for stepping up!</h2>
              <p className="text-base text-[#52525B] leading-relaxed mb-6">
                We&rsquo;ve got your details and sent a confirmation to your email. The volunteers team will be in touch when the next event starts shaping up. Genuinely &mdash; we couldn&rsquo;t do this without you.
              </p>
              <button
                type="button"
                onClick={() => setSubmitted(false)}
                data-testid="volunteer-another-btn"
                className="text-sm font-bold uppercase tracking-wider text-[#F0A500] hover:text-[#D99500]"
              >
                Add another volunteer &rarr;
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} data-testid="volunteer-form" className="space-y-8">
              <div className="bg-white p-6 sm:p-8 border border-zinc-200 space-y-6">
                <h3 className="text-xl font-bold text-[#0A0A0A]">Your details</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <Label htmlFor="v-name" className="text-sm font-semibold text-[#0A0A0A] mb-1.5 block">
                      Name <span className="text-[#D62F2F]">*</span>
                    </Label>
                    <Input id="v-name" data-testid="volunteer-name" required value={form.name} onChange={update('name')} />
                  </div>
                  <div>
                    <Label htmlFor="v-email" className="text-sm font-semibold text-[#0A0A0A] mb-1.5 block">
                      Email <span className="text-[#D62F2F]">*</span>
                    </Label>
                    <Input id="v-email" data-testid="volunteer-email" required type="email" value={form.email} onChange={update('email')} />
                  </div>
                  <div>
                    <Label htmlFor="v-phone" className="text-sm font-semibold text-[#0A0A0A] mb-1.5 block">
                      Contact number <span className="text-[#D62F2F]">*</span>
                    </Label>
                    <Input id="v-phone" data-testid="volunteer-phone" required inputMode="tel" value={form.phone} onChange={update('phone')} />
                  </div>
                  <div>
                    <Label htmlFor="v-location" className="text-sm font-semibold text-[#0A0A0A] mb-1.5 block">
                      Town / area <span className="text-[#71717A] font-normal">(optional)</span>
                    </Label>
                    <Input id="v-location" data-testid="volunteer-location" placeholder="e.g. Glossop, Hadfield" value={form.location} onChange={update('location')} />
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 sm:p-8 border border-zinc-200 space-y-4">
                <div>
                  <h3 className="text-xl font-bold text-[#0A0A0A]">Which events?</h3>
                  <p className="text-sm text-[#52525B] mt-1">Tick everything you&rsquo;d be happy to help with.</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {EVENTS.map((ev) => (
                    <ChoiceCard
                      key={ev.id}
                      testid={`volunteer-event-${ev.id}`}
                      checked={events.has(ev.id)}
                      onChange={(c) => toggle(events, setEvents)(ev.id, c)}
                      label={ev.label}
                      blurb={ev.blurb}
                    />
                  ))}
                </div>
              </div>

              <div className="bg-white p-6 sm:p-8 border border-zinc-200 space-y-4">
                <div>
                  <h3 className="text-xl font-bold text-[#0A0A0A]">What would you like to do?</h3>
                  <p className="text-sm text-[#52525B] mt-1">Pick whatever suits — &ldquo;wherever needed&rdquo; is always welcome.</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {ROLES.map((r) => (
                    <ChoiceCard
                      key={r.id}
                      testid={`volunteer-role-${r.id}`}
                      checked={roles.has(r.id)}
                      onChange={(c) => toggle(roles, setRoles)(r.id, c)}
                      label={r.label}
                      blurb={r.blurb}
                    />
                  ))}
                </div>
              </div>

              <div className="bg-white p-6 sm:p-8 border border-zinc-200 space-y-5">
                <h3 className="text-xl font-bold text-[#0A0A0A]">Anything else?</h3>
                <div>
                  <Label htmlFor="v-availability" className="text-sm font-semibold text-[#0A0A0A] mb-1.5 block">
                    Availability notes <span className="text-[#71717A] font-normal">(optional)</span>
                  </Label>
                  <Input id="v-availability" data-testid="volunteer-availability" placeholder="e.g. Weekends only / Not during August" value={form.availability} onChange={update('availability')} />
                </div>
                <div>
                  <Label htmlFor="v-message" className="text-sm font-semibold text-[#0A0A0A] mb-1.5 block">
                    Message <span className="text-[#71717A] font-normal">(optional)</span>
                  </Label>
                  <Textarea
                    id="v-message"
                    data-testid="volunteer-message"
                    rows={4}
                    placeholder="Any questions, specific skills, or notes for the team."
                    value={form.message}
                    onChange={update('message')}
                  />
                </div>
              </div>

              {error && <p data-testid="volunteer-error" className="text-sm text-red-600 font-semibold">{error}</p>}

              <label className="flex items-center gap-3 p-4 bg-white border border-zinc-200 cursor-pointer select-none" data-testid="volunteer-not_robot-wrap">
                <Checkbox checked={notRobot} onCheckedChange={(c) => setNotRobot(c === true)} data-testid="volunteer-not_robot" />
                <span className="text-sm font-semibold text-[#0A0A0A]">I&rsquo;m not a robot</span>
              </label>

              <button
                type="submit"
                disabled={!canSubmit}
                data-testid="volunteer-submit"
                className="btn-amber w-full text-center disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? 'Sending…' : 'Sign me up to help'}
              </button>

              <p className="text-xs text-[#71717A] text-center">
                We&rsquo;ll only use your details to coordinate volunteering &mdash; no marketing, no sharing.
              </p>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
